/**
 * Tender slice — Appels d'Offres premium subsystem.
 *
 * 01/06/2026 — Replaces the old `comp-bid` event modal with a real
 * subscription-driven tender funnel.
 *
 * State :
 *   • activeTenders                 array of tender instances currently
 *                                   visible on the AO board
 *   • activeApplication             the player's in-progress application
 *                                   { tenderId, stage, originalOffer,
 *                                     counterOffer, probability }
 *   • tenderHistory                 won/lost log (capped at 30 entries)
 *
 * Stages :
 *   - 'preparing'   default — player tweaking offer in the modal
 *   - 'pending'     offer submitted, waiting for daily roll (8-14 days)
 *   - 'counter'     client returned a counter-offer, awaiting decision
 *   - 'won' / 'lost' archived in tenderHistory, removed from board
 *
 * Daily refresh : called from coreSlice.nextDay. Drops expired entries
 * (> 30 days on board with no application) and adds new ones up to 3
 * concurrent (eligible-filtered).
 */

import { generateId } from '../helpers';
import { TENDERS } from '../../data/tenders';
import {
  getAvailableTenders,
  computeTenderWinProbability,
  rollTenderOutcome,
  buildContractFromTender,
} from '../helpers/tenderHelpers';
import {
  buildTenderAnnounceMemo,
  buildTenderSubmittedMemo,
  buildTenderCounterMemo,
  buildTenderWonMemo,
  buildTenderLostMemo,
  buildTenderExpiryMemo,
  hasCommercialSupport,
} from '../helpers/tenderNarrative';
import { COMPETITORS } from '../../data/competitors';
import { RELATION_DELTAS, findCompetitorIdByName } from '../../utils/competitorRelations';
import { computeTenderPricingPatch } from '../../data/economyBalance';

const TENDER_BOARD_MAX = 3;
const TENDER_EXPIRY_DAYS = 30;
// 08/02/2026 — Q2 Phase 2 user prod : la taille du board AO scale avec la
// progression du joueur. P1 (local) → 3 ; P2 (régional) → 4 ; P3+ → 5.
// Justification narrative : plus la boîte grandit, plus le radar commercial
// captera de signaux/AO. Plus il y a de signal sur le board, plus le
// joueur peut élaborer une stratégie (et plus la concurrence se manifeste
// — on capte plus d'AO mais on en gagne pas plus à expertise égale).
function getTenderBoardMaxForPhase(phase = 1) {
  if (phase >= 3) return 5;
  if (phase >= 2) return 4;
  return TENDER_BOARD_MAX;
}
// 05/02/2026 — Q1 retour user prod : délai de décision client réduit à
// 2 jours fixes après soumission d'offre (était 8-14 jours).
const TENDER_DECISION_DELAY_MIN = 2;
const TENDER_DECISION_DELAY_MAX = 2;
const COUNTER_DISCOUNT = 0.92; // client shaves 8 % off the player price
// 12/02/2026 — Rétro-effet "presse spécialisée" : quand un seuil
// marketScale vient d'être franchi, les concurrents durcissent leur
// pricing pendant 30 jours et le client tire davantage sur le prix.
const COUNTER_DISCOUNT_AGGRESSIVE = 0.88; // 12 % off au lieu de 8 %
// 04/02/2026 — Q1 Bug Messagerie : délai strict pour staffer un AO gagné.
// Au-delà, le client perd patience → réputation -10 + contrat annulé.
// Rappel automatique dans la messagerie à J-5 et J-2.
const TENDER_STAFF_DEADLINE_DAYS = 10;
const TENDER_STAFF_REMINDER_DAYS = [5, 2];

export const createTenderSlice = (set, get) => ({
  activeTenders: [],
  activeApplications: [],   // array of { id, tenderId, originalOffer, counterOffer, probability, stage, decisionDay }
  tenderHistory: [],

  // 05/02/2026 — Q1 engagement : dernier événement de résolution d'AO,
  // consommé par AoResultNotifier (toast + son + mini-clip Sora 2 pour les
  // wins premium). Le composant le reset à `null` après affichage.
  lastAoResultEvent: null,

  consumeLastAoResultEvent: () => set({ lastAoResultEvent: null }),

  /**
   * 08/02/2026 — Q2 Lot C user prod : événements « Appel d'offre
   * inattendu » (evt-opp-01) et « Appel d'offre public » (evt-opp-03)
   * doivent désormais alimenter le vrai circuit AO au lieu d'appliquer
   * un bonus immédiat. Cette action injecte un tender ad-hoc directement
   * dans `activeTenders`, en ignorant l'éligibilité statique (le client
   * a déjà spontanément choisi le joueur — la sélection est faite par
   * l'event). Le tender se comporte ensuite comme un AO classique
   * (soumission → roll → won/lost).
   *
   * @param {Object} tenderTemplate
   *   { id, clientName, sector, tier, durationDays, baseDailyRevenue,
   *     requiresFleet, requiresRseTier, requiresMedicalPct,
   *     competitors, competitorPriceRange, narrative, source? }
   * @param {Object} opts
   *   { expiryDays = 15, skipAnnounceMemo = false }
   * @returns {{ ok: boolean, instanceId?: string, reason?: string }}
   */
  injectAdHocTender: (tenderTemplate, opts = {}) => {
    if (!tenderTemplate || !tenderTemplate.id) {
      return { ok: false, reason: 'invalid_template' };
    }
    const state = get();
    const today = state.totalDays || 0;
    const expiryDays = Math.max(3, opts.expiryDays || 15);
    // On évite d'injecter 2 instances simultanées du même id.
    if ((state.activeTenders || []).some(t => t.id === tenderTemplate.id)) {
      return { ok: false, reason: 'already_on_board' };
    }
    const instance = {
      ...tenderTemplate,
      // 18/07/2026 — revalorisation AO : +30 % + plancher rentabilité 20 %.
      ...computeTenderPricingPatch(tenderTemplate, state),
      instanceId: generateId(),
      spawnDay: today,
      expiresOn: today + expiryDays,
      source: tenderTemplate.source || 'event',
      isAdHoc: true,
    };
    set({ activeTenders: [...(state.activeTenders || []), instance] });

    if (!opts.skipAnnounceMemo) {
      const pushMsg = get().pushCustomMessage;
      if (pushMsg) {
        try {
          pushMsg({
            ...buildTenderAnnounceMemo(instance, state.hiredSupport || {}),
            // Mention explicite : tender ad-hoc, fenêtre courte.
            subject: `📣 Opportunité AO — ${instance.clientName} (à saisir sous ${expiryDays} j)`,
            ttlDays: expiryDays + 1,
          });
        } catch (err) { console.warn('[tender] ad-hoc announce push failed:', err); }
      }
    }
    return { ok: true, instanceId: instance.instanceId };
  },

  /**
   * Daily refresh — called from coreSlice.nextDay.
   *   • Drops board entries older than TENDER_EXPIRY_DAYS with NO active
   *     application (so the player can take their time).
   *   • Adds fresh tenders up to TENDER_BOARD_MAX, picking from the
   *     eligible catalogue and skipping any already on board OR with an
   *     active application OR already in recent history (cooldown 60 d).
   *   • Pushes one announcement mémo per newly-added tender (via
   *     enqueueDeferredMessage to the next day so it lands during the
   *     player's review of the tick).
   */
  refreshTenders: () => {
    const state = get();
    if (!state.gameStarted) return;
    const today = state.totalDays || 0;
    const board = state.activeTenders || [];
    const apps = state.activeApplications || [];

    // Drop stale entries without an application.
    const appliedIds = new Set(apps.map(a => a.tenderId));
    let fresh = board.filter(t => appliedIds.has(t.id) || (today - (t.spawnDay || 0)) <= TENDER_EXPIRY_DAYS);

    // ── 16/06/2026 — Alerte de clôture imminente (fonctions commerciales) ──
    // Quand un AO sans candidature arrive à ≤ 5 jours de sa date limite, le
    // signataire commercial (Sylvie / Marc / Léa) envoie un rappel en
    // Messagerie. Une seule fois par instance (flag `expiryAlertSent`).
    // 25/07/2026 (retour user prod) — le rappel n'existe QUE si un
    // commercial est recruté : c'est un avantage concret du recrutement.
    // Sans commercial, pas de flag posé → si le joueur recrute pendant la
    // fenêtre de 5 j, l'alerte peut encore partir.
    const expiryPush = get().pushCustomMessage;
    const canAlertExpiry = hasCommercialSupport(state.hiredSupport || {});
    fresh = fresh.map(t => {
      const daysLeft = (t.expiresOn || 0) - today;
      if (canAlertExpiry && !t.expiryAlertSent && !appliedIds.has(t.id) && daysLeft > 0 && daysLeft <= 5) {
        if (expiryPush) {
          try {
            expiryPush({
              ...buildTenderExpiryMemo(t, state.hiredSupport || {}, daysLeft),
              ttlDays: daysLeft + 1,
            });
          } catch (err) { console.warn('[tender] expiry alert push failed:', err); }
        }
        return { ...t, expiryAlertSent: true };
      }
      return t;
    });

    if (fresh.length >= getTenderBoardMaxForPhase(state.phase)) {
      set({ activeTenders: fresh });
      return;
    }

    // 05/02/2026 (Q1 Lot 2) — Salon marketing : si le joueur a investi dans
    // un stand standard/premium, des prospects AO bonus sont débloqués. Ils
    // se traduisent ici par +1/+2 places supplémentaires sur le board pour
    // l'appel `refreshTenders` courant (consommés au passage).
    const salonBonus = state.salonProspectTenders || 0;
    const effectiveMax = getTenderBoardMaxForPhase(state.phase) + Math.max(0, Math.min(2, salonBonus));

    // Catalogue → eligible candidates.
    const eligible = getAvailableTenders(state);
    const onBoardIds = new Set(fresh.map(t => t.id));
    const historyCutoff = today - 60;
    const recentHistoryIds = new Set(
      (state.tenderHistory || [])
        .filter(h => (h.resolvedDay || 0) >= historyCutoff)
        .map(h => h.tenderId)
    );
    let candidates = eligible.filter(t =>
      !onBoardIds.has(t.id) && !recentHistoryIds.has(t.id) && !appliedIds.has(t.id)
    );
    // 24/07/2026 (choix user) — Convois exceptionnels RARES : jamais plus
    // d'un sur le board, et 50 % de chance d'apparaître à chaque refresh.
    // En contrepartie ils rapportent désormais 70-130 k€ (cf.
    // data/exceptionalTenders.js) + retombées notoriété/média à la clé.
    const convoyCandidates = candidates.filter(t => t.kind === 'convoy_exceptional');
    if (convoyCandidates.length > 0) {
      const alreadyOnBoard = fresh.some(t => t.kind === 'convoy_exceptional');
      const luckyOne = (!alreadyOnBoard && Math.random() < 0.5)
        ? convoyCandidates[Math.floor(Math.random() * convoyCandidates.length)]
        : null;
      candidates = candidates.filter(t => t.kind !== 'convoy_exceptional');
      if (luckyOne) candidates.push(luckyOne);
    }
    if (candidates.length === 0) {
      set({ activeTenders: fresh });
      return;
    }

    candidates.sort(() => Math.random() - 0.5);
    const need = effectiveMax - fresh.length;
    const newOnes = candidates.slice(0, need).map(template => ({
      ...template,
      // 18/07/2026 — revalorisation AO : +30 % + plancher rentabilité 20 %
      // basé sur les coûts réels de la flotte du joueur au moment du spawn.
      ...computeTenderPricingPatch(template, state),
      instanceId: generateId(),
      spawnDay: today,
      expiresOn: today + TENDER_EXPIRY_DAYS,
    }));

    fresh = [...fresh, ...newOnes];
    // Consume the salon bonus by how many EXTRA slots were filled beyond
    // the base TENDER_BOARD_MAX.
    const extraFromSalon = Math.max(0, fresh.length - getTenderBoardMaxForPhase(state.phase));
    const salonConsumed = Math.min(salonBonus, extraFromSalon);
    if (salonConsumed > 0) {
      set({ activeTenders: fresh, salonProspectTenders: Math.max(0, salonBonus - salonConsumed) });
    } else {
      set({ activeTenders: fresh });
    }

    // Push announcement mémos for the new tenders.
    const hiredSupport = state.hiredSupport || {};
    const pushMsg = get().pushCustomMessage;
    if (pushMsg) {
      newOnes.forEach(t => {
        try {
          pushMsg({
            ...buildTenderAnnounceMemo(t, hiredSupport),
            ttlDays: 7,
          });
        } catch (err) { console.warn('[tender] announce push failed:', err); }
      });
    }
  },

  /**
   * Submit a player offer on a tender.
   *
   * @param {string} tenderInstanceId
   * @param {Object} offer  { dailyPrice, durationDays, hseCommitment }
   * @returns {{ ok: boolean, reason?: string, probability?: number }}
   */
  submitTenderOffer: (tenderInstanceId, offer) => {
    const state = get();
    const tender = (state.activeTenders || []).find(t => t.instanceId === tenderInstanceId);
    if (!tender) return { ok: false, reason: 'tender_not_found' };
    // Already an application for this tender?
    if ((state.activeApplications || []).some(a => a.tenderId === tender.id)) {
      return { ok: false, reason: 'already_applied' };
    }
    // Validate offer bounds — price only. Duration is FIXED per tender
    // (set by the client) and is forced to tender.durationDays below.
    if (offer.dailyPrice < Math.round(tender.baseDailyRevenue * 0.70) ||
        offer.dailyPrice > Math.round(tender.baseDailyRevenue * 1.30)) {
      return { ok: false, reason: 'price_out_of_bounds' };
    }
    // Snap duration to the tender's fixed value, regardless of what the
    // caller submitted.
    const effectiveOffer = {
      ...offer,
      durationDays: tender.durationDays,
    };
    const { probability } = computeTenderWinProbability(state, tender, effectiveOffer);
    const decisionDay = (state.totalDays || 0) +
      TENDER_DECISION_DELAY_MIN +
      Math.floor(Math.random() * (TENDER_DECISION_DELAY_MAX - TENDER_DECISION_DELAY_MIN + 1));
    const application = {
      id: generateId(),
      tenderId: tender.id,
      tenderInstanceId,
      tenderSnapshot: tender,
      originalOffer: effectiveOffer,
      counterOffer: null,
      probability,
      stage: 'pending',
      submittedDay: state.totalDays || 0,
      decisionDay,
    };
    set({ activeApplications: [...(state.activeApplications || []), application] });

    const pushMsg = get().pushCustomMessage;
    if (pushMsg) {
      try {
        pushMsg({
          ...buildTenderSubmittedMemo(tender, state.hiredSupport || {}, effectiveOffer, probability),
          ttlDays: 14,
        });
      } catch (err) { console.warn('[tender] submitted push failed:', err); }
    }
    return { ok: true, probability };
  },

  /**
   * Daily resolution — called from coreSlice.nextDay AFTER the day flip.
   * Walks `activeApplications`, resolves those whose decisionDay <= today,
   * pushes the appropriate mémo, and either drops them or stages a
   * counter-offer.
   */
  resolvePendingApplications: () => {
    const state = get();
    const today = state.totalDays || 0;
    const apps = state.activeApplications || [];
    if (apps.length === 0) return;

    const stillPending = [];
    let updatedTenders = state.activeTenders || [];
    let updatedHistory = state.tenderHistory || [];
    // 02/06/2026 — Accumulate newly-won contracts locally rather than
    // calling set() inside the loop. The previous implementation read
    // state.availableContracts from the stale snapshot on every iteration,
    // so two wins in the same tick would lose the first contract.
    const newContracts = [];
    // 05/02/2026 — Q1 engagement : on retient le dernier événement de
    // résolution pour le toast/son/clip (priorité aux wins national tier).
    let lastEvent = null;
    const eventPriority = (e) => {
      if (!e) return 0;
      if (e.outcome === 'won' && e.tier === 'national') return 3;
      if (e.outcome === 'won') return 2;
      if (e.outcome === 'counter') return 1;
      return 0;
    };
    const pushMsg = get().pushCustomMessage;
    const hiredSupport = state.hiredSupport || {};
    // 12/02/2026 — Fenêtre "concurrence renforcée" post-narratif presse
    // (voir marketScaleNarrative). Boost la proba de contre-offre et le
    // shave du client sur les 30 jours suivant le franchissement d'un
    // seuil marketScale.
    const aggressionActive = (state.competitorAggressionUntilDay || 0) > today;
    const counterDiscount = aggressionActive ? COUNTER_DISCOUNT_AGGRESSIVE : COUNTER_DISCOUNT;

    for (const app of apps) {
      if (app.stage !== 'pending' || today < app.decisionDay) {
        stillPending.push(app); continue;
      }
      const tender = app.tenderSnapshot;
      const outcome = rollTenderOutcome(app.probability, Math.random, aggressionActive);
      if (outcome === 'won') {
        // 04/06/2026 — Spot tenders create an exclusivity ENGAGEMENT
        // (90-day window with random spot calls) instead of a regular
        // long-running contract. The engagement spawns calls on its own
        // tick — no contract is added to activeContracts here.
        if (tender.spot && typeof get().startSpotEngagement === 'function') {
          try { get().startSpotEngagement(tender); }
          catch (err) { console.warn('[spotEngagement] start failed:', err); }
          updatedTenders = updatedTenders.filter(t => t.instanceId !== app.tenderInstanceId);
          updatedHistory = [
            { tenderId: tender.id, outcome: 'won', resolvedDay: today,
              clientName: tender.clientName,
              offer: app.originalOffer, probability: app.probability, spot: true },
            ...updatedHistory.slice(0, 29),
          ];
          // Relation diplomatique (Phase 2) — spot tender remporté.
          if (typeof get().adjustCompetitorRelation === 'function' && Array.isArray(tender.competitors)) {
            tender.competitors.forEach((name) => {
              const cid = findCompetitorIdByName(COMPETITORS, name);
              if (cid) get().adjustCompetitorRelation(cid, RELATION_DELTAS.TENDER_WON_AGAINST, `spot_won_${tender.id}`);
            });
          }
          continue;
        }
        const contract = {
          ...buildContractFromTender(tender, app.originalOffer, state),
          instanceId: generateId(),
          spawnDay: today,
          // 28/07/2026 — Bug prod « valeur totale appliquée en gain
          // journalier » : `revenue` est un montant JOURNALIER pour les
          // contrats réguliers (acceptContract le lit en priorité). La
          // valeur totale du marché vit dans `totalValue`.
          totalValue: app.originalOffer.dailyPrice * app.originalOffer.durationDays,
          // 04/02/2026 — Q1 Bug B Messagerie : délai strict pour staffer.
          tenderStaffDeadline: today + TENDER_STAFF_DEADLINE_DAYS,
          tenderStaffRemindersSent: [],
        };
        newContracts.push(contract);
        updatedTenders = updatedTenders.filter(t => t.instanceId !== app.tenderInstanceId);
        updatedHistory = [
          { tenderId: tender.id, outcome: 'won', resolvedDay: today,
            clientName: tender.clientName, contractInstanceId: contract.instanceId,
            offer: app.originalOffer, probability: app.probability },
          ...updatedHistory.slice(0, 29),
        ];
        if (pushMsg) {
          try {
            pushMsg({ ...buildTenderWonMemo(tender, hiredSupport, app.originalOffer), ttlDays: 14 });
          } catch (err) { console.warn('[tender] won push failed:', err); }
        }
        // 17/06/2026 (Lot 6 Phase 2) — Relation diplomatique : chaque
        // concurrent mentionné comme adversaire prend -5 (tender_won).
        if (typeof get().adjustCompetitorRelation === 'function' && Array.isArray(tender.competitors)) {
          tender.competitors.forEach((name) => {
            const cid = findCompetitorIdByName(COMPETITORS, name);
            if (cid) get().adjustCompetitorRelation(cid, RELATION_DELTAS.TENDER_WON_AGAINST, `tender_won_${tender.id}`);
          });
        }
        // 05/02/2026 — Q1 engagement : publish AO result event.
        const evt = {
          outcome: 'won',
          tier: tender.tier || 'regional',
          clientName: tender.clientName,
          tenderId: tender.id,
          resolvedOnDay: today,
        };
        if (eventPriority(evt) >= eventPriority(lastEvent)) lastEvent = evt;
      } else if (outcome === 'counter') {
        // Stage a counter offer at COUNTER_DISCOUNT × player's price.
        // 12/02/2026 — Shave élargi (12 % au lieu de 8 %) pendant la
        // fenêtre agressive post-narratif presse.
        const counterOffer = {
          dailyPrice: Math.round(app.originalOffer.dailyPrice * counterDiscount),
          durationDays: app.originalOffer.durationDays,
          hseCommitment: app.originalOffer.hseCommitment,
        };
        stillPending.push({ ...app, stage: 'counter', counterOffer });
        if (pushMsg) {
          try {
            pushMsg({ ...buildTenderCounterMemo(tender, hiredSupport, app.originalOffer, counterOffer), ttlDays: 14 });
          } catch (err) { console.warn('[tender] counter push failed:', err); }
        }
        // 05/02/2026 — Q1 engagement.
        const evt = {
          outcome: 'counter', tier: tender.tier || 'regional',
          clientName: tender.clientName, tenderId: tender.id, resolvedOnDay: today,
        };
        if (eventPriority(evt) >= eventPriority(lastEvent)) lastEvent = evt;
      } else {
        // 'lost'
        updatedTenders = updatedTenders.filter(t => t.instanceId !== app.tenderInstanceId);
        updatedHistory = [
          { tenderId: tender.id, outcome: 'lost', resolvedDay: today,
            clientName: tender.clientName, offer: app.originalOffer, probability: app.probability },
          ...updatedHistory.slice(0, 29),
        ];
        // 08/02/2026 — Q2 Phase 2 user prod : attribuer la défaite à un
        // concurrent nommé + son dirigeant pour donner de la chair au
        // marché. Le nom est tiré des `tender.competitors[]` (filtrés
        // sur ceux pour lesquels on a des données dirigeant) ou, à
        // défaut, l'un des COMPETITORS principaux. Le narratif passe
        // dans `buildTenderLostMemo` via un override soft.
        let winnerCompetitor = null;
        if (Array.isArray(tender.competitors) && tender.competitors.length > 0) {
          // Préférence aux concurrents principaux (avec dirigeant) si présents.
          for (const name of tender.competitors) {
            const cid = findCompetitorIdByName(COMPETITORS, name);
            if (cid) {
              const c = COMPETITORS.find(cc => cc.id === cid);
              if (c && c.director) { winnerCompetitor = c; break; }
            }
          }
        }
        // Fallback : tirer au hasard un des 4 grands concurrents.
        if (!winnerCompetitor) {
          winnerCompetitor = COMPETITORS[Math.floor(Math.random() * COMPETITORS.length)];
        }
        if (pushMsg) {
          try {
            const memo = buildTenderLostMemo(tender, hiredSupport);
            // On enrichit le body avec une attribution explicite.
            const winnerLine =
              `\n\n— **Rumeur de marché** : selon nos sources, l'AO a été remporté ` +
              `par **${winnerCompetitor.name}**` +
              (winnerCompetitor.director ? ` (${winnerCompetitor.director.name}, ${winnerCompetitor.director.role})` : '') +
              `.`;
            pushMsg({ ...memo, body: (memo.body || '') + winnerLine, ttlDays: 14 });
          } catch (err) { console.warn('[tender] lost push failed:', err); }
        }
        // 17/06/2026 (Lot 6 Phase 2) — Relation diplomatique : la défaite
        // sur un AO améliore légèrement la relation avec les concurrents
        // (+2) car le rival se sent valorisé d'avoir gagné contre vous.
        if (typeof get().adjustCompetitorRelation === 'function' && Array.isArray(tender.competitors)) {
          tender.competitors.forEach((name) => {
            const cid = findCompetitorIdByName(COMPETITORS, name);
            if (cid) get().adjustCompetitorRelation(cid, RELATION_DELTAS.TENDER_LOST_AGAINST, `tender_lost_${tender.id}`);
          });
        }
        // 05/02/2026 — Q1 engagement.
        const evt = {
          outcome: 'lost', tier: tender.tier || 'regional',
          clientName: tender.clientName, tenderId: tender.id, resolvedOnDay: today,
          // 08/02/2026 — Q2 Phase 2 : on remonte aussi l'identité du
          // concurrent gagnant pour que l'AoResultNotifier puisse
          // l'afficher dans la modale premium.
          winnerCompetitorId: winnerCompetitor?.id,
          winnerCompetitorName: winnerCompetitor?.name,
          winnerDirectorName: winnerCompetitor?.director?.name,
        };
        if (eventPriority(evt) >= eventPriority(lastEvent)) lastEvent = evt;
      }
    }

    set({
      activeApplications: stillPending,
      activeTenders: updatedTenders,
      tenderHistory: updatedHistory,
      // 02/06/2026 — Merge all newly-won contracts in a single set so we
      // don't lose any when 2+ resolve as 'won' in the same tick.
      ...(newContracts.length > 0
        ? { availableContracts: [...(state.availableContracts || []), ...newContracts] }
        : {}),
      // 05/02/2026 — Q1 engagement : publish last AO result event.
      ...(lastEvent ? { lastAoResultEvent: lastEvent } : {}),
    });

    // 08/02/2026 — Refonte phases (Q2). AO national gagné → vidéo Sora 2
    // dédiée (ao-national-win) en plus du clip aléatoire de l'AoResultNotifier.
    // Ne s'affiche qu'une fois par game (premier AO national gagné).
    if (lastEvent && lastEvent.outcome === 'won' && lastEvent.tier === 'national'
      && typeof get().triggerMilestone === 'function') {
      const fired = get().triggerMilestone('ao_national_win_first', '/marche');
      if (fired && typeof get().triggerMilestoneVideo === 'function') {
        try {
          get().triggerMilestoneVideo({
            kind: 'ao-national-win',
            reason: `Premier AO national remporté chez ${lastEvent.clientName}`,
          });
        } catch (err) { console.warn('[tenderSlice] AO national video failed:', err); }
      }
    }
  },

  /**
   * Player accepts the client's counter-offer (stage: 'counter').
   */
  acceptCounterOffer: (applicationId) => {
    const state = get();
    const app = (state.activeApplications || []).find(a => a.id === applicationId);
    if (!app || app.stage !== 'counter') return { ok: false, reason: 'no_counter' };
    const tender = app.tenderSnapshot;
    const contract = {
      ...buildContractFromTender(tender, app.counterOffer, state),
      instanceId: generateId(),
      spawnDay: state.totalDays || 0,
      // 28/07/2026 — même fix que resolvePendingApplications : la valeur
      // totale du marché vit dans totalValue, jamais dans revenue.
      totalValue: app.counterOffer.dailyPrice * app.counterOffer.durationDays,
      // 04/02/2026 — Q1 Bug B Messagerie : délai strict pour staffer.
      tenderStaffDeadline: (state.totalDays || 0) + TENDER_STAFF_DEADLINE_DAYS,
      tenderStaffRemindersSent: [],
    };
    set({
      availableContracts: [...(state.availableContracts || []), contract],
      activeApplications: (state.activeApplications || []).filter(a => a.id !== applicationId),
      activeTenders: (state.activeTenders || []).filter(t => t.instanceId !== app.tenderInstanceId),
      tenderHistory: [
        { tenderId: tender.id, outcome: 'won', resolvedDay: state.totalDays || 0,
          clientName: tender.clientName, contractInstanceId: contract.instanceId,
          offer: app.counterOffer, viaCounter: true, probability: app.probability },
        ...(state.tenderHistory || []).slice(0, 29),
      ],
    });
    const pushMsg = get().pushCustomMessage;
    if (pushMsg) {
      try {
        pushMsg({ ...buildTenderWonMemo(tender, state.hiredSupport || {}, app.counterOffer), ttlDays: 14 });
      } catch (err) { console.warn('[tender] accept-counter push failed:', err); }
    }
    return { ok: true };
  },

  /**
   * Player rejects the client's counter-offer → tender is lost.
   */
  rejectCounterOffer: (applicationId) => {
    const state = get();
    const app = (state.activeApplications || []).find(a => a.id === applicationId);
    if (!app || app.stage !== 'counter') return { ok: false, reason: 'no_counter' };
    const tender = app.tenderSnapshot;
    set({
      activeApplications: (state.activeApplications || []).filter(a => a.id !== applicationId),
      activeTenders: (state.activeTenders || []).filter(t => t.instanceId !== app.tenderInstanceId),
      tenderHistory: [
        { tenderId: tender.id, outcome: 'lost', resolvedDay: state.totalDays || 0,
          clientName: tender.clientName, offer: app.originalOffer,
          rejectedCounter: app.counterOffer, probability: app.probability },
        ...(state.tenderHistory || []).slice(0, 29),
      ],
    });
    const pushMsg = get().pushCustomMessage;
    if (pushMsg) {
      try {
        pushMsg({ ...buildTenderLostMemo(tender, state.hiredSupport || {}), ttlDays: 14 });
      } catch (err) { console.warn('[tender] reject-counter push failed:', err); }
    }
    return { ok: true };
  },

  /**
   * 04/02/2026 — Q1 Bug B Messagerie : « gagner un AO sans assigner de
   * ressources passe sans effet ». Désormais, chaque AO gagné doit être
   * staffé dans les `TENDER_STAFF_DEADLINE_DAYS` (10 jours par défaut).
   * Sinon le client annule + réputation −10.
   *
   * Daily tick — pour chaque contrat `tenderWon` dans `availableContracts` :
   *   • Si daysLeft ∈ TENDER_STAFF_REMINDER_DAYS (5, 2) → push un rappel
   *     dans la Messagerie (signé par le commercial), une seule fois par
   *     palier (tenderStaffRemindersSent).
   *   • Si daysLeft <= 0 → annulation : retrait de availableContracts,
   *     réputation -10, push message d'annulation + entrée dans
   *     tenderHistory (outcome 'cancelled_no_staff').
   *
   * Appelé depuis coreSlice.nextDay (avec les autres ticks AO).
   */
  tickTenderStaffingDeadline: () => {
    const state = get();
    const today = state.totalDays || 0;
    const wonContracts = (state.availableContracts || []).filter(
      c => c.tenderWon && typeof c.tenderStaffDeadline === 'number'
    );
    if (wonContracts.length === 0) return { reminded: 0, cancelled: 0 };

    const pushMsg = get().pushCustomMessage;
    const hiredSupport = state.hiredSupport || {};
    const expiredIds = new Set();
    const updatedAvailable = (state.availableContracts || []).map(c => c);
    let reminded = 0;
    let cancelled = 0;
    const historyAdds = [];
    let reputationPenalty = 0;

    wonContracts.forEach((c) => {
      const daysLeft = (c.tenderStaffDeadline || 0) - today;
      const idx = updatedAvailable.findIndex(x => x.instanceId === c.instanceId);
      if (idx < 0) return;
      if (daysLeft <= 0) {
        // Annulation : client perd patience.
        expiredIds.add(c.instanceId);
        cancelled += 1;
        reputationPenalty += 10;
        historyAdds.push({
          tenderId: c.tenderId,
          outcome: 'cancelled_no_staff',
          resolvedDay: today,
          clientName: c.client || c.clientName,
          reason: 'staff_deadline_missed',
        });
        if (pushMsg) {
          try {
            pushMsg({
              senderType: 'client',
              senderName: c.client || c.clientName || 'Client AO',
              tone: 'aggressive',
              kind: 'info',
              subject: `Marché annulé — délai dépassé`,
              body:
                `Bonjour,\n\nNous vous avions attribué le marché **« ${c.name } »** ` +
                `il y a ${TENDER_STAFF_DEADLINE_DAYS} jours, en attente de votre démarrage opérationnel ` +
                `(affectation camion + chauffeur). Sans nouvelles ni mobilisation de ressources de ` +
                `votre part, nous sommes contraints d'**annuler le marché** et de le reconfier à un ` +
                `concurrent.\n\n` +
                `**Conséquences :**\n` +
                `• Réputation −10 (notification client diffusée)\n` +
                `• Marché perdu (${(c.totalValue || (c.dailyRevenue || 0) * (c.duration || 0)).toLocaleString('fr-FR')} € de CA potentiel)\n\n` +
                `Cordialement,\n${c.client || c.clientName || 'Client'}`,
              ttlDays: 14,
            });
          } catch (err) { console.warn('[tender] staff cancel push failed:', err); }
        }
      } else if (TENDER_STAFF_REMINDER_DAYS.includes(daysLeft)) {
        const alreadySent = Array.isArray(c.tenderStaffRemindersSent)
          && c.tenderStaffRemindersSent.includes(daysLeft);
        if (!alreadySent) {
          reminded += 1;
          // Mettre à jour le contrat (rappel envoyé) en place.
          updatedAvailable[idx] = {
            ...c,
            tenderStaffRemindersSent: [...(c.tenderStaffRemindersSent || []), daysLeft],
          };
          if (pushMsg) {
            try {
              const signer = hiredSupport['role-sylvie-marchand']
                ? 'Sylvie Marchand, Directrice commerciale'
                : hiredSupport['role-marc-dupuis']
                  ? 'Marc Dupuis, Commercial terrain'
                  : 'Léa Bertrand, Commerciale junior';
              pushMsg({
                senderType: 'client',
                senderName: c.client || c.clientName || 'Client AO',
                tone: daysLeft <= 2 ? 'urgent' : 'formal',
                kind: 'info',
                subject: `⏳ Rappel — staffer le marché « ${c.client || c.clientName} » sous ${daysLeft} j`,
                body:
                  `Bonjour,\n\nNous restons en attente du démarrage opérationnel du marché ` +
                  `**« ${c.name} »** qui vous a été attribué. Il vous reste **${daysLeft} jour${daysLeft > 1 ? 's' : ''}** ` +
                  `pour affecter un camion + un chauffeur à ce contrat ` +
                  `(onglet **Appels d'Offres** → section "AO gagnés à staffer", ou Dashboard → ` +
                  `Contrats disponibles).\n\n` +
                  `${daysLeft <= 2
                    ? `⚠️ **Délai critique** : sans démarrage sous 48 h, nous serons contraints ` +
                      `d'annuler le marché (réputation −10) et de le reconfier à un concurrent.`
                    : `Sans staffing avant la date limite, le marché sera annulé (réputation −10).`}\n\n` +
                  `Cordialement,\n— ${signer}`,
                ttlDays: daysLeft + 1,
              });
            } catch (err) { console.warn('[tender] staff reminder push failed:', err); }
          }
        }
      }
    });

    if (expiredIds.size === 0 && reminded === 0) return { reminded: 0, cancelled: 0 };

    set({
      availableContracts: updatedAvailable.filter(c => !expiredIds.has(c.instanceId)),
      reputation: Math.max(0, (state.reputation || 0) - reputationPenalty),
      tenderHistory: [...historyAdds, ...(state.tenderHistory || [])].slice(0, 30),
    });
    return { reminded, cancelled };
  },
});
